import { DocumentItem } from '@/components/modals/DocumentModal';
import fetcher, { FleetFile, FleetResponse } from '../fetcher';

/**
 * Params for searching purchase orders
 */
export type SearchPurchaseOrderParams = {
	pagination: {
		pageSize: number;
		pageNumber: number;
	};
	search?: {
		by: string;
		value: string;
	};
};

/**
 * Item type for a purchase order in the list
 */
export type SearchPurchaseOrderItem = {
	mklNo: string;
	spfNo: string;
	spfId: number;
	napResolutionId: number;
	mppNo: string;
	customerName: string;
	customerId: string;
	bmppGroupName: string;
	mppId: number;
	allowDetail: boolean;
};

/**
 * Response type for searching purchase orders
 */
export type SearchPurchaseOrderResponse = {
	totalCount: number;
	pageSize: number;
	currentPage: number;
	totalPages: number;
	items: SearchPurchaseOrderItem[];
};

/**
 * Search purchase orders via API
 * @param params - Search params
 * @returns Promise with search result
 */
export const searchPurchaseOrders = (params: SearchPurchaseOrderParams) => {
	return fetcher<SearchPurchaseOrderResponse>({
		api: '/purchase-orders/search-customer',
		body: params,
	});
};

export type GetPurchaseOrderDetailParams = {
	pagination: {
		pageSize: number;
		pageNumber: number;
	};
	search?: {
		by: string;
		value: string;
	};
	mppId?: number;
	poReleaseDate?: string;
	poExpiredDate?: string;
	poStatus?: string;
};

export type PurchaseOrderDetailItem = {
	applicationId: string;
	agreementNo: string | null;
	creditSimulationId: number;
	creditSimulationNo: string;
	agreementPrintingNo: string;
	agreementPrintingId: number;
	purchaseOrderId: number;
	purchaseOrderNo: string;
	purchaseOrderExtendDate: Date;
	supplierName: string;
	brand: string;
	facilityId: number;
	purchaseOrderReleaseDate: Date;
	purchaseOrderExpiredDate: Date;
	purchaseOrderStatus: string;
	customerName: string;
	totalUnit: number;
	remainingUnit: number;
	status: string;
	createdAt: string;
	allowCreatePo: boolean;
	allowSelect: boolean;
	allowRetrieve: boolean;
	allowExtend: boolean;
	requestType: string;
	napResolutionId: number;
	spfId: number;
	ntfAmount: number;
};

export type GetPurchaseOrderDetailResponse = {
	mppId: number;
	mppNo: string;
	customerName: string;
	customerId: string;
	bmppGroupId: string;
	bmppGroupName: string;
	details: PurchaseOrderDetailItem[];
	options: {
		statusOptions: Array<{ id: string; value: string }>;
		searchByOptions: Array<{ id: string; value: string }>;
		supplierOptions: Array<{ id: string; value: string }>;
		brandsOptions: Array<{ id: string; value: string }>;
	};
};

export const getPurchaseOrderDetail = (
	params: GetPurchaseOrderDetailParams,
) => {
	return fetcher<GetPurchaseOrderDetailResponse>({
		api: '/purchase-orders/get-detail',
		body: params,
	});
};

export const getPurchaseOrderActionDetail = (
	params: GetPurchaseOrderDetailParams,
) => {
	return fetcher<GetPurchaseOrderDetailResponse>({
		api: '/purchase-orders/get-action-detail',
		body: params,
	});
};

export type SubmitPurchaseOrderParams = {
	purchaseOrderId: number;
	creditSimulationId: number;
	unit: number;
	isIncludedCostComponent: boolean;
};

export type SubmitPurchaseOrderResponse = {
	purchaseOrderNo: string;
	plakatNo: string;
};

export const submitPurchaseOrder = (params: SubmitPurchaseOrderParams) => {
	return fetcher<SubmitPurchaseOrderResponse>({
		api: '/purchase-orders/submit',
		body: params,
	});
};

export type RetrievePurchaseOrderRequest = {
	purchaseOrderId: number;
	extendRequestDocument: FleetFile;
	purchaseOrderDocument: FleetFile;
	notes: string;
};

export const retrievePurchaseOrder = (params: RetrievePurchaseOrderRequest) => {
	return fetcher({
		api: '/purchase-orders/submit-retrieve-request',
		body: params,
	});
};

export type ExtendPurchaseOrderRequest = {
	purchaseOrderId: number;
	extendRequestDocument: FleetFile;
	purchaseOrderDocument: FleetFile;
	notes: string;
};

export const extendPurchaseOrder = (params: ExtendPurchaseOrderRequest) => {
	return fetcher({
		api: '/purchase-orders/submit-extend-request',
		body: params,
	});
};

export type CancelPurchaseOrderRequest = {
	purchaseOrderId: number;
	cancelledUnit: number;
	purchaseOrderDocument: FleetFile;
	notes: string;
};

export const cancelPurchaseOrder = (params: CancelPurchaseOrderRequest) => {
	return fetcher({
		api: '/purchase-orders/submit-cancel-request',
		body: params,
	});
};

export type SubmitDisbursementPurchaseOrderRequest = {
	purchaseOrderIds: number[];
};

export const submitDisbursementPurchaseOrder = (
	params: SubmitDisbursementPurchaseOrderRequest,
) => {
	return fetcher({
		api: '/purchase-orders/submit-disbursement-request',
		body: params,
	});
};

export type PrintPurchaseOrderRequest = {
	purchaseOrderIds: number[];
};

export type PrintPurchaseOrderResponse = DocumentItem[];

export const printPurchaseOrder = (params: PrintPurchaseOrderRequest) => {
	return fetcher<PrintPurchaseOrderResponse>({
		api: '/purchase-orders/download-document',
		body: params,
	});
};

export const getPurchaseOrderTodoCount = () => {
	return fetcher<number>({
		api: '/purchase-orders/get-things-to-do',
		method: 'POST',
	});
};

export type POApprovalListItem = {
	id: number;
	requestType: number;
	requestTypeDisplay: string;
	purchaseOrderId: number;
	purchaseOrderNo: string;
	purchaseOrderDocument: {
		id: string;
		name: string;
		path: string;
		url: string;
		format: string;
	};
	extendRequestDocument: {
		id: string;
		name: string;
		path: string;
		url: string;
		format: string;
	};
	approvalAction: number;
	approvalActionDisplay: string;
	frmName: string;
	frmNotes: string;
	fbdhNotes: string;
	creditSimulationId: number;
	creditSimulationNo: string;
	facilityId: number;
	supplierName: string;
	brand: string;
	purchaseOrderReleaseDate: Date;
	purchaseOrderExpiredDate: Date;
	purchaseOrderExtendDate: Date;
	purchaseOrderStatus: string;
	spfId: number;
	spfNo: string;
	agreementPrintingId: number;
	agreementPrintingNo: string;
	customerName: string;
	totalUnit: number;
	remainingUnit: number;
	status: string;
	createdAt: Date;
	lastModifiedAt: Date;
};

export type GetPOApprovalListResponse = POApprovalListItem[];

export type GetPOApprovalListRequest = {
	id: number;
};

export const getPOApprovalList = (body: GetPOApprovalListRequest) => {
	return fetcher<GetPOApprovalListResponse>({
		api: '/purchase-orders/get-fbdh-approval-list',
		body,
	});
};

export type ProcessPOApprovalRequest = {
	approvals: {
		approvalId: number;
		requestType: string;
		approvalAction: string;
		fbdhNotes: string;
	}[];
};

export const processPOApproval = (params: ProcessPOApprovalRequest) => {
	return fetcher({
		api: '/purchase-orders/process-approval',
		body: params,
	});
};

export interface PODetailSearchOptionsResponse {
	statusOptions: Array<{ id: string; value: string }>;
	searchByOptions: Array<{ id: string; value: string }>;
	supplierOptions: Array<{ id: string; value: string }>;
	brandsOptions: Array<{ id: string; value: string }>;
}

export const getPurchaseOrderDetailSearchOptions = () =>
	fetcher<PODetailSearchOptionsResponse>({
		api: '/purchase-orders/get-detail-search-options',
	});

export const cancelPO = (poId: string) =>
	fetcher<FleetResponse<null>>({
		api: `/purchase-order/${poId}/cancel`,
		body: {},
	});
